import {
    CssBaseline,
    Grid,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import IndividualExperiencePhotos from '../../../components/Experiences/IndividualExperiencePhotos';
import IndividualExperienceContent from '../../../components/Experiences/IndividualExperienceContent';
import IndividualExperienceHero from '../../../components/Experiences/IndividualExperienceHero';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useGetIndividualAssetQuery } from '../../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import LoadingComponent from '../../../../layout/styledcomponents/LoadingAnimations/LoadingComponent';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectVisitTypeSliceData } from '../../../../app/Slices/VisitTypeSlice';

export default function IndividualExperiencePage() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isIpad = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const urlRef = window.location.href.split('/')[5];
    const location = useLocation();
    const visitTypeSelected: any = useSelector(selectVisitTypeSliceData);

    const [isCalendarVisible, setIsCalendarVisible] = useState(false);
    const [didClickReserve, setDidClickReserve] = useState(false);

    const onClickGoBack = () => {
        if (location?.state?.component === 'reports') {
            navigate(`/${location?.state?.url}`);
        } else {
            navigate(`/viewExperiences/${visitTypeSelected?.view}`);
        }
    };

    const {
        data: getIndividualAsset,
        isFetching,
        error,
    } = useGetIndividualAssetQuery(`modules/${urlRef}`);

    if (error) {
        navigate(`/*`);
    }

    useEffect(() => {
        if (!isMobile) return;

        const target = document.getElementById('calendarSection-mobile');
        if (!target) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsCalendarVisible(entry.isIntersecting);
            },
            { root: null, threshold: 0.15 },
        );

        observer.observe(target);
        return () => observer.disconnect();
    }, [isMobile]);

    useEffect(() => {
        if (!isMobile) return;
        if (!isCalendarVisible) {
            setDidClickReserve(false);
        }
    }, [isCalendarVisible, isMobile]);

    const scrollToSection = (id: string, offset = 96) => {
        const el = document.getElementById(id);
        if (!el) return;
        const y = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: y, behavior: 'smooth' });
    };

    const handleReserveNow = () => {
        setDidClickReserve(true);
        scrollToSection('calendarSection-mobile', 96);
    };

    return (
        <>
            <CssBaseline />
            <Grid
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'left',
                    alignItems: 'center',
                    [theme.breakpoints.down('md')]: {
                        mt: '-130px',
                    },
                }}
            >
                {isFetching ? (
                    <Grid
                        sx={{
                            height: '100%',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <LoadingComponent />
                    </Grid>
                ) : (
                    <Grid
                        sx={{
                            maxWidth: theme.breakpoints.values.md,
                            height: '100%',
                            backgroundColor: 'white.main',
                            justifyContent: isMobile ? 'center' : 'left',
                            alignItems: 'center',
                        }}
                    >
                        <Grid
                            sx={{
                                width: '100%',
                                display: { xs: 'none', md: 'flex' },
                            }}
                        >
                            <Typography variant='h1'>
                                {getIndividualAsset?.name}
                            </Typography>
                        </Grid>

                        <Grid
                            sx={{
                                textAlign: isMobile ? 'center' : 'left',
                                paddingBottom: isMobile ? '10px' : '0px',
                                display: { xs: 'none', md: 'flex' },
                            }}
                        >
                            <IndividualExperienceHero
                                experience={getIndividualAsset}
                            />
                        </Grid>

                        <Grid>
                            <Grid
                                sx={{
                                    position: 'relative',
                                    [theme.breakpoints.down('md')]: {
                                        top: '28px',
                                        mx: 'calc(50% - 50vw)',
                                    },
                                    zIndex: { xs: 10, md: 'auto' },
                                }}
                            >
                                <IndividualExperiencePhotos
                                    experience={getIndividualAsset}
                                />
                            </Grid>

                            <Grid
                                sx={{
                                    position: 'relative',
                                    [theme.breakpoints.down('md')]: {
                                        width: '100vw',
                                        mx: 'calc(50% - 50vw)',
                                        boxShadow: '0 -4px 8px rgba(0,0,0,0.1)',
                                        borderRadius: '16px 16px 0 0',
                                        zIndex: 12,
                                    },
                                }}
                            >
                                <IndividualExperienceContent
                                    experience={getIndividualAsset}
                                />
                            </Grid>
                        </Grid>

                        <Grid
                            sx={{
                                width: '100%',
                                height: isMobile ? '80px' : '15%',
                                display: isMobile || isIpad ? 'none' : 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                paddingTop: '40px',
                                paddingBottom: isIpad ? '160px' : '90px',
                            }}
                        >
                            {location.state && (
                                <CustomButton
                                    onClick={onClickGoBack}
                                    color='primary'
                                    sx={{
                                        marginLeft: '-8px',
                                        '&:hover': {
                                            cursor: 'pointer',
                                            color: 'primary.main',
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                >
                                    <ArrowBackIcon />
                                    <Typography
                                        sx={{
                                            fontSize: '12px',
                                            paddingLeft: '5px',
                                        }}
                                    >
                                        Back to{' '}
                                        {location?.state?.component ===
                                        'reports'
                                            ? 'report'
                                            : 'experiences'}
                                    </Typography>
                                </CustomButton>
                            )}
                        </Grid>
                    </Grid>
                )}
                {isMobile && !isCalendarVisible && !didClickReserve && (
                    <CustomButton
                        onClick={handleReserveNow}
                        variant='contained'
                        type='button'
                        sx={{
                            position: 'fixed',
                            bottom: 0,
                            left: 0,
                            width: '100%',
                            padding: '15px',
                            borderRadius: 0,
                            zIndex: 1000,
                            color: theme.palette.custom.button.secondary
                                .textColor,
                            backgroundColor:
                                theme.palette.custom.button.secondary
                                    .backgroundColor,
                            pb: 'calc(15px + env(safe-area-inset-bottom))',
                            '&:hover': {
                                backgroundColor:
                                    theme.palette.custom.button.primary
                                        .hoverBackgroundColor,
                            },
                        }}
                    >
                        Reserve Now
                    </CustomButton>
                )}
            </Grid>
        </>
    );
}
